import CancellableHelper from "@/requests/CancellableHelper";
import { ExchangeRate } from "@/models/api/ExchangeRate";
import { CurrencyItemsResponse, CurrencyItems } from "@/models/api/administration/exchangeRate/CurrencyItems";
import {
  ExchangeRateItemsResponse,
  ExchangeRateItemsAdd,
} from "@/models/api/administration/exchangeRate/ExchangeRateItems";
import GenericTableRequest from "@/models/api/GenericTableRequest";

//#region Routes
export const URL_GET_EXCHANGE_RATE_BY_DATE_AND_CURRENCY = "/Currency/${CurrencyId}/ExchangeRates/${ExchangeDate}";
export const URL_GET_EXCHANGE_LAST_AVAILABLE_RATE_BY_DATE_AND_CURRENCY =
  "/Currency/LastAvailableExchangeRateByDateAndCurrency";
export const URL_GET_EXCHANGE_LAST_AVAILABLE_RATE_BY_DATE_AND_CURRENCY_BY_STRING =
  "/Currency/LastAvailableExchangeRateByDateAndCurrencyByString";
export const URL_GET_CALCULATED_VALUE_FROM_VALUE_IN_CURRENCY = "/Currency/CalculateValueFromValueInCurrency";
export const URL_GET_CALCULATED_VALUE_IN_CURRENCY_FROM_VALUE = "/Currency/CalculateValueInCurrencyFromValue";
export const URL_GET_CURRENCIES_LIST_PAGED = "/ExchangeRate/CurrenciesList/Paged";
export const URL_GET_EXCHANGE_RATE_PAGED = "/ExchangeRate/ExchangeRateList/${CurrencyId}/Paged";
export const URL_POST_EDIT_CURRENCY = "/ExchangeRate/EditCurrency";
export const URL_POST_ADD_CURRENCY = "/ExchangeRate/AddCurrency";
export const URL_POST_DELETE_EXCHANGE_RATE = "/ExchangeRate/DeleteExchangeRate";
export const URL_POST_ADD_EXCHANGE_RATE = "/ExchangeRate/AddExchangeRateManually";
export const URL_GET_EXCHANGE_RATE_COUNT = "/ExchangeRate/ExchangeRateCount";
export const URL_POST_DELETE_CURRENCY = "/ExchangeRate/DeleteCurrency";
//#endregion

const _getExchangeRate = new CancellableHelper<ExchangeRate>(URL_GET_EXCHANGE_RATE_BY_DATE_AND_CURRENCY, ExchangeRate);
export const getExchangeRate = async (currencyId: number, exchangeDate: string): Promise<ExchangeRate> => {
  return await _getExchangeRate.get({
    CurrencyId: currencyId,
    ExchangeDate: exchangeDate,
  });
};

const _getExchangeRateLastAvailableByString = new CancellableHelper<ExchangeRate>(
  URL_GET_EXCHANGE_LAST_AVAILABLE_RATE_BY_DATE_AND_CURRENCY_BY_STRING,
  ExchangeRate
);
export const getExchangeRateLastAvailableByString = async (
  currency: string,
  exchangeDate: string
): Promise<ExchangeRate> => {
  return await _getExchangeRateLastAvailableByString.get({
    currency: currency,
    exchangeDate: exchangeDate,
  });
};

const _getExchangeRateLastAvailable = new CancellableHelper<ExchangeRate>(
  URL_GET_EXCHANGE_LAST_AVAILABLE_RATE_BY_DATE_AND_CURRENCY,
  ExchangeRate
);
export const getExchangeRateLastAvailable = async (currencyId: number, exchangeDate: string): Promise<ExchangeRate> => {
  return await _getExchangeRateLastAvailable.get({
    currencyId: currencyId,
    exchangeDate: exchangeDate,
  });
};

const _getCalculatedValueFromValueInCurrency = new CancellableHelper<number>(
  URL_GET_CALCULATED_VALUE_FROM_VALUE_IN_CURRENCY
);
export const getCalculatedValueFromValueInCurrency = async (
  valueInCurrency: number,
  exchangeRate: number
): Promise<number> => {
  return await _getCalculatedValueFromValueInCurrency.get({
    ValueInCurrency: valueInCurrency,
    ExchangeRate: exchangeRate,
  });
};

const _getCalculatedValueInCurrencyFromValue = new CancellableHelper<number>(
  URL_GET_CALCULATED_VALUE_IN_CURRENCY_FROM_VALUE
);
export const getCalculatedValueInCurrencyFromValue = async (value: number, exchangeRate: number): Promise<number> => {
  return await _getCalculatedValueInCurrencyFromValue.get({
    Value: value,
    ExchangeRate: exchangeRate,
  });
};

const _getCurrencyList = new CancellableHelper<CurrencyItemsResponse>(
  URL_GET_CURRENCIES_LIST_PAGED,
  CurrencyItemsResponse
);
_getCurrencyList.jsonSerializer = true;
export const getCurrencyList = async (genericTableRequest: GenericTableRequest): Promise<CurrencyItemsResponse> => {
  return await _getCurrencyList.get(genericTableRequest);
};

const _getExchangeRateList = new CancellableHelper<ExchangeRateItemsResponse>(
  URL_GET_EXCHANGE_RATE_PAGED,
  ExchangeRateItemsResponse
);
_getExchangeRateList.jsonSerializer = true;
export const getExchangeRateList = async (
  genericTableRequest: GenericTableRequest,
  currencyId: number
): Promise<ExchangeRateItemsResponse> => {
  const test = await _getExchangeRateList.get({
    ...genericTableRequest,
    CurrencyId: currencyId,
  });
  return test;
};

export const editCurrency = async (model: CurrencyItems): Promise<null> => {
  const _editCurrency = new CancellableHelper<null>(URL_POST_EDIT_CURRENCY, null, true);
  return await _editCurrency.post(null, model);
};

export const addCurrency = async (model: CurrencyItems): Promise<null> => {
  const _addCurrency = new CancellableHelper<null>(URL_POST_ADD_CURRENCY, null, true);
  return await _addCurrency.post(null, model);
};

export const deleteCurrency = async (currencyId: number): Promise<null> => {
  const _deleteCurrency = new CancellableHelper<null>(URL_POST_DELETE_CURRENCY, null, true);
  return await _deleteCurrency.post({ currencyId: currencyId }, null);
};

export const deleteExchangeRate = async (exchangeRateId: number): Promise<null> => {
  const _deleteExchangeRate = new CancellableHelper<null>(URL_POST_DELETE_EXCHANGE_RATE, null, true);
  return await _deleteExchangeRate.post({ exchangeRateId: exchangeRateId }, null);
};

export const addExchangeRate = async (model: ExchangeRateItemsAdd, currencyId: number): Promise<null> => {
  const _addExchangeRate = new CancellableHelper<null>(URL_POST_ADD_EXCHANGE_RATE, null, true);
  return await _addExchangeRate.post({ currencyId: currencyId }, model);
};

export const downloadExchangeRateCount = async (currencyId: number): Promise<number> => {
  const _getExchangeRateCount = new CancellableHelper<number>(URL_GET_EXCHANGE_RATE_COUNT);
  return await _getExchangeRateCount.get({ currencyId: currencyId });
};
